// Edit-only mode: a project/library that failed because its video has no
// speech can become one clip of a minute the user picks. These rules mirror the
// backend (src/lib/editOnly/editOnlyRules.js) so the picker shows exactly the
// minute the backend will cut. `source_no_audio` is not eligible yet.
export const EDIT_ONLY_FAILURE_CODES: ReadonlySet<string> = new Set([
  'transcript_empty',
  'transcript_too_sparse',
]);

export const EDIT_ONLY_WINDOW_SECONDS = 60;
export const EDIT_ONLY_MIN_SECONDS = 1;

export function isEditOnlyEligible(failureCode?: string | null): boolean {
  return !!failureCode && EDIT_ONLY_FAILURE_CODES.has(failureCode);
}

export interface EditOnlySelection {
  start: number;
  end: number;
}

const rangeOffset = (rangeStart: number) => (Number.isFinite(rangeStart) && rangeStart > 0 ? rangeStart : 0);

// A ranged project/library keeps the full source video: its duration is the
// range length and the segment is in absolute source time from rangeStart.
// With endSeconds the user chose both ends (1 to 60 seconds); without it, the
// minute from startSeconds, or the whole range when it is a minute or less.
export function editOnlyWindow(
  durationSeconds: number,
  startSeconds: number,
  rangeStart = 0,
  endSeconds?: number,
): EditOnlySelection {
  const offset = rangeOffset(rangeStart);
  const rangeEnd = offset + durationSeconds;
  if (endSeconds !== undefined) {
    const latestStart = Math.max(offset, rangeEnd - EDIT_ONLY_MIN_SECONDS);
    const start = Number.isFinite(startSeconds) && startSeconds > offset ? Math.min(startSeconds, latestStart) : offset;
    const shortestEnd = Math.min(start + EDIT_ONLY_MIN_SECONDS, rangeEnd);
    const longestEnd = Math.min(start + EDIT_ONLY_WINDOW_SECONDS, rangeEnd);
    const end = Number.isFinite(endSeconds) ? Math.min(Math.max(endSeconds, shortestEnd), longestEnd) : shortestEnd;
    return { start, end };
  }
  if (durationSeconds <= EDIT_ONLY_WINDOW_SECONDS) return { start: offset, end: rangeEnd };
  const latestStart = offset + durationSeconds - EDIT_ONLY_WINDOW_SECONDS;
  const start = Number.isFinite(startSeconds) && startSeconds > offset ? Math.min(startSeconds, latestStart) : offset;
  return { start, end: start + EDIT_ONLY_WINDOW_SECONDS };
}

// The first minute of the video (or range), or all of it when shorter.
export function defaultEditOnlySelection(durationSeconds: number, rangeStart = 0): EditOnlySelection {
  const offset = rangeOffset(rangeStart);
  return { start: offset, end: offset + Math.min(durationSeconds, EDIT_ONLY_WINDOW_SECONDS) };
}

// Applies a two-handle slider change: the handle that moved goes where it was
// put, and the other one is dragged along so the segment stays between 1 and
// 60 seconds, inside the video (or range).
export function adjustEditOnlySelection(
  previous: EditOnlySelection,
  next: [number, number],
  { durationSeconds, rangeStart = 0 }: { durationSeconds: number; rangeStart?: number },
): EditOnlySelection {
  const offset = rangeOffset(rangeStart);
  const rangeEnd = offset + durationSeconds;
  const clamp = (value: number) => Math.min(Math.max(value, offset), rangeEnd);
  let start = clamp(next[0]);
  let end = clamp(next[1]);
  if (next[0] !== previous.start) {
    if (end - start > EDIT_ONLY_WINDOW_SECONDS) end = start + EDIT_ONLY_WINDOW_SECONDS;
    if (end - start < EDIT_ONLY_MIN_SECONDS) {
      end = Math.min(start + EDIT_ONLY_MIN_SECONDS, rangeEnd);
      start = Math.max(offset, Math.min(start, end - EDIT_ONLY_MIN_SECONDS));
    }
  } else {
    if (end - start > EDIT_ONLY_WINDOW_SECONDS) start = end - EDIT_ONLY_WINDOW_SECONDS;
    if (end - start < EDIT_ONLY_MIN_SECONDS) {
      start = Math.max(end - EDIT_ONLY_MIN_SECONDS, offset);
      end = Math.min(rangeEnd, Math.max(end, start + EDIT_ONLY_MIN_SECONDS));
    }
  }
  return { start, end };
}

// Like formatDuration, but 0 is a real position ("00:00"), not a missing value.
export function formatWindowTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${two(hours)}:${two(minutes)}:${two(secs)}` : `${two(minutes)}:${two(secs)}`;
}
